import toast from "react-hot-toast";
import { type IntlShape, useIntl } from "react-intl";

import { Link } from "~/components/link";
import { Button } from "~/components/ui/button";
import { useURL } from "~/contexts";

import {
  type CtaType,
  type QuizStatus,
  ctaTypes,
  quizStatuses,
} from "./constants";
import messages from "./messages";
import type { NextModuleTarget } from "./use-learning-center-module";

/**
 * SFCC-11505 — port of composable `ctaSection.jsx`. Footer beneath a submitted
 * quiz: FAILED → "Redo Quiz"; PASSED → optional "See My Points" (when a level
 * just completed) plus the next-step CTA whose label depends on `ctaType`.
 */
interface CtaSectionProps {
  quizStatus: QuizStatus;
  setQuizStatus: (status: QuizStatus) => void;
  requestInProgress: boolean;
  goToNextModule: (target: NextModuleTarget) => Promise<boolean>;
  nextModuleData: NextModuleTarget;
  /** False until the next-step CTA has a target; the button stays disabled. */
  isNextReady: boolean;
  ctaType: CtaType | null;
  isLevelComplete: boolean;
}

const getCtaText = (ctaType: CtaType | null, intl: IntlShape): string => {
  if (ctaType === ctaTypes.NEXT_GOAL)
    return intl.formatMessage(messages.ctaFindMyNextGoal);
  if (ctaType === ctaTypes.RETURN_TO_GOAL)
    return intl.formatMessage(messages.ctaReturnToGoal);
  return intl.formatMessage(messages.ctaNextModule);
};

const CtaSection = ({
  quizStatus,
  setQuizStatus,
  requestInProgress,
  goToNextModule,
  nextModuleData,
  isNextReady,
  ctaType,
  isLevelComplete,
}: CtaSectionProps) => {
  const url = useURL();
  const intl = useIntl();

  return (
    <div
      className="mt-[3.25rem] flex flex-col lg:flex-row lg:justify-end"
      data-testid="ctaSection"
    >
      {quizStatus === quizStatuses.FAILED && (
        <Button
          type="button"
          variant="cta"
          className="w-full lg:w-[12.25rem]"
          onClick={() => setQuizStatus(quizStatuses.OPEN)}
          data-testid="ctaSection-Redo"
        >
          {intl.formatMessage(messages.ctaRedoQuiz)}
        </Button>
      )}

      {quizStatus === quizStatuses.PASSED && (
        <>
          {isLevelComplete && (
            <Button
              asChild
              variant="secondary"
              className="w-full lg:w-[12.25rem]"
              data-testid="ctaSection-SeePoints"
            >
              {/* /loyalty-perks is an SFRA route — the hybrid Link full-navigates. */}
              <Link to={url("/loyalty-perks")}>
                {intl.formatMessage(messages.ctaSeeMyPoints)}
              </Link>
            </Button>
          )}
          <Button
            type="button"
            variant="cta"
            className="mt-4 w-full lg:ml-4 lg:mt-0 lg:w-[12.25rem]"
            onClick={async () => {
              // A click that can't navigate must say so, never no-op (COM-588).
              if (!(await goToNextModule(nextModuleData))) {
                toast.error(intl.formatMessage(messages.nextStepError));
              }
            }}
            disabled={requestInProgress || !isNextReady}
            data-testid="ctaSection-Next"
          >
            {getCtaText(ctaType, intl)}
          </Button>
        </>
      )}
    </div>
  );
};

export default CtaSection;
